import { AlertTriangle, Globe, Plus, Trash2, Upload } from "lucide-react";
import { useId, useState } from "react";
import { toast } from "sonner";
import { Alert, Button, Input, Modal, Select } from "@/components/common";
import {
  useAddOwuSource,
  useDeleteOwu,
  useDeleteOwuSource,
  useInsurerContacts,
  useOwuSources,
  useUploadOwu,
} from "@/hooks/useToolkit";
import type { OwuDocument } from "@/types/toolkit";
import { formatFileSize } from "./format";

const MAX_BYTES = 30 * 1024 * 1024;

function errorMessage(error: unknown, fallback: string) {
  return (error as { message?: string } | null)?.message ?? fallback;
}

/** Insurers from the contacts list — the same set the panel knows. */
function useInsurerOptions() {
  const { data } = useInsurerContacts();
  return (data ?? []).map((i) => ({
    value: i.insurerId,
    label: i.insurerName,
  }));
}

export function UploadOwuModal({
  isOpen,
  products,
  onClose,
}: {
  isOpen: boolean;
  products: string[];
  onClose: () => void;
}) {
  const upload = useUploadOwu();
  const insurers = useInsurerOptions();
  const fileInputId = useId();
  const [insurerId, setInsurerId] = useState("");
  const [product, setProduct] = useState("");
  const [name, setName] = useState("");
  const [versionLabel, setVersionLabel] = useState("");
  const [validFrom, setValidFrom] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const fileError =
    file && file.size > MAX_BYTES
      ? "Plik jest za duży — maksymalnie 30 MB."
      : undefined;
  const valid =
    insurerId !== "" &&
    product !== "" &&
    name.trim().length > 0 &&
    file !== null &&
    !fileError;

  function handleClose() {
    upload.reset();
    setInsurerId("");
    setProduct("");
    setName("");
    setVersionLabel("");
    setValidFrom("");
    setFile(null);
    onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid || !file) {
      return;
    }
    upload.mutate(
      {
        details: {
          insurerId,
          product,
          name: name.trim(),
          versionLabel: versionLabel.trim(),
          validFrom,
        },
        file,
      },
      {
        onSuccess: (saved) => {
          toast.success(`Dodano OWU: ${saved.name} (${saved.insurerName}).`);
          handleClose();
        },
      },
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Dodaj OWU ręcznie">
      <form onSubmit={handleSubmit} className="space-y-4">
        {upload.error && (
          <Alert variant="error">
            {errorMessage(upload.error, "Nie udało się dodać OWU")}
          </Alert>
        )}
        <Select
          label="Towarzystwo"
          options={insurers}
          value={insurerId}
          onChange={setInsurerId}
          placeholder="Wybierz towarzystwo"
        />
        <Select
          label="Produkt"
          options={products.map((p) => ({ value: p, label: p }))}
          value={product}
          onChange={setProduct}
          placeholder="Wybierz produkt"
        />
        <Input
          label="Nazwa dokumentu"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
          placeholder="np. OWU Autocasco"
          required
          maxLength={250}
          autoComplete="off"
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Wersja (opcjonalnie)"
            value={versionLabel}
            onChange={(e) => {
              setVersionLabel(e.target.value);
            }}
            placeholder="np. AC/2026/01"
            maxLength={100}
            autoComplete="off"
          />
          <Input
            label="Obowiązuje od (opcjonalnie)"
            type="date"
            value={validFrom}
            onChange={(e) => {
              setValidFrom(e.target.value);
            }}
          />
        </div>
        <p className="text-xs text-dim">
          Jeśli to nowa wersja OWU, które już są w bibliotece (to samo
          towarzystwo, produkt i nazwa), poprzednia trafi do archiwum.
        </p>

        <div>
          <label
            htmlFor={fileInputId}
            className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-edge p-3 text-sm text-dim hover:bg-muted"
          >
            <Upload size={18} aria-hidden className="shrink-0" />
            <span className="min-w-0 truncate">
              {file
                ? `${file.name} (${formatFileSize(file.size)})`
                : "Wybierz plik PDF (do 30 MB)"}
            </span>
          </label>
          <input
            id={fileInputId}
            type="file"
            accept=".pdf,application/pdf"
            className="sr-only"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
            }}
          />
          {fileError && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
              {fileError}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-edge-soft pt-4">
          <Button variant="ghost" type="button" onClick={handleClose}>
            Anuluj
          </Button>
          <Button type="submit" disabled={!valid} isLoading={upload.isPending}>
            Dodaj
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function DeleteOwuModal({
  document,
  onClose,
}: {
  document: OwuDocument | null;
  onClose: () => void;
}) {
  const remove = useDeleteOwu();

  function handleClose() {
    remove.reset();
    onClose();
  }

  return (
    <Modal isOpen={document !== null} onClose={handleClose} title="Usuń OWU">
      <div className="space-y-4">
        {remove.error && (
          <Alert variant="error">
            {errorMessage(remove.error, "Nie udało się usunąć OWU")}
          </Alert>
        )}
        <div className="flex gap-3">
          <AlertTriangle
            size={20}
            aria-hidden
            className="shrink-0 text-amber-500"
          />
          <p className="text-sm text-sub">
            Usunąć <strong>{document?.name}</strong> ({document?.insurerName})?
            Użyj tylko dla pliku dodanego przez pomyłkę — nowsze wersje OWU same
            przenoszą poprzednie do archiwum.
            {document?.isCurrent &&
              " Jeśli jest wcześniejsza wersja, znów stanie się aktualna."}
          </p>
        </div>
        <div className="flex justify-end gap-3 border-t border-edge-soft pt-4">
          <Button variant="ghost" onClick={handleClose}>
            Anuluj
          </Button>
          <Button
            variant="danger"
            isLoading={remove.isPending}
            onClick={() => {
              if (document) {
                remove.mutate(document.id, {
                  onSuccess: () => {
                    toast.success("Usunięto OWU.");
                    handleClose();
                  },
                });
              }
            }}
          >
            Usuń
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** The allowlist: the weekly check reads and downloads OWU only from these domains. */
export function OwuSourcesModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { data: sources } = useOwuSources(isOpen);
  const insurers = useInsurerOptions();
  const add = useAddOwuSource();
  const remove = useDeleteOwuSource();
  const [insurerId, setInsurerId] = useState("");
  const [domain, setDomain] = useState("");
  const [startUrl, setStartUrl] = useState("");

  function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!insurerId || !domain.trim()) {
      return;
    }
    add.mutate(
      { insurerId, domain: domain.trim(), startUrl: startUrl.trim() },
      {
        onSuccess: (saved) => {
          toast.success(`Dodano domenę ${saved.domain}.`);
          setDomain("");
          setStartUrl("");
        },
      },
    );
  }

  const error = add.error ?? remove.error;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        add.reset();
        remove.reset();
        onClose();
      }}
      title="Oficjalne strony towarzystw"
      className="max-w-2xl"
    >
      <div className="space-y-4">
        <p className="text-sm text-dim">
          Cotygodniowe sprawdzanie czyta strony i pobiera OWU wyłącznie z tych
          domen (razem z subdomenami), więc do biblioteki nie trafią dokumenty z
          porównywarek ani forów. Jeśli strona główna towarzystwa nie prowadzi
          do OWU, podaj adres strony z listą OWU.
        </p>
        {error && (
          <Alert variant="error">
            {errorMessage(error, "Nie udało się zapisać zmian")}
          </Alert>
        )}
        <form
          onSubmit={handleAdd}
          className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end"
        >
          <Select
            label="Towarzystwo"
            options={insurers}
            value={insurerId}
            onChange={setInsurerId}
            placeholder="Wybierz"
          />
          <Input
            label="Domena"
            value={domain}
            onChange={(e) => {
              setDomain(e.target.value);
            }}
            placeholder="np. warta.pl"
            autoComplete="off"
          />
          <Button
            type="submit"
            leftIcon={<Plus size={14} aria-hidden />}
            disabled={!insurerId || !domain.trim()}
            isLoading={add.isPending}
          >
            Dodaj
          </Button>
          <Input
            label="Strona z OWU (opcjonalnie)"
            value={startUrl}
            onChange={(e) => {
              setStartUrl(e.target.value);
            }}
            placeholder="np. https://www.warta.pl/dokumenty/owu"
            autoComplete="off"
            className="sm:col-span-3"
          />
        </form>
        <ul className="max-h-80 divide-y divide-edge-soft overflow-y-auto rounded-lg border border-edge-soft">
          {(sources ?? []).map((s) => (
            <li key={s.id} className="flex items-center gap-3 px-3 py-2">
              <Globe size={14} aria-hidden className="shrink-0 text-faint" />
              <span className="min-w-0 flex-1 text-sm">
                <span className="font-medium text-main">{s.domain}</span>
                <span className="text-dim"> · {s.insurerName}</span>
                {s.startUrl && (
                  <span className="block truncate text-xs text-faint">
                    start: {s.startUrl}
                  </span>
                )}
              </span>
              <button
                type="button"
                aria-label={`Usuń ${s.domain} (${s.insurerName})`}
                title="Usuń"
                onClick={() => {
                  remove.mutate(s.id);
                }}
                className="rounded-md p-1.5 text-faint hover:bg-muted hover:text-main focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-adara-500"
              >
                <Trash2 size={14} aria-hidden />
              </button>
            </li>
          ))}
          {sources?.length === 0 && (
            <li className="px-3 py-3 text-sm text-faint">
              Brak domen — automatyczne wyszukiwanie OWU nie ma gdzie szukać.
            </li>
          )}
        </ul>
      </div>
    </Modal>
  );
}
